import { useLingui } from '@lingui/react/macro';

import Box from '@/design_system/Box';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import {
  isArchived,
  isInDraftStep,
  isInPaymentStep,
  isInRefundStep,
  isRequiredPaidEstimateAdvanceDeposit,
  useClientAmountDue,
  useDepositOptions,
} from '@/models/request';
import { useRequestContext } from '@/routes/Requests/contexts/RequestContext';

import {
  ClientTransactionDetails,
  ClientTransactionPartialPaymentWarning,
  isFinalPaymentStep,
} from './ClientTransactionDetails';

export const ClientAmountDueBox = () => {
  const { t } = useLingui();
  const { request } = useRequestContext();

  const displayClientAmountDueBox = useDisplayClientAmountDueBox();

  const { data } = useClientAmountDue({
    requestId: request.id,
    enabled: displayClientAmountDueBox,
  });

  const prices = data?.prices ?? [];
  const amountDue = prices.reduce((acc, curr) => acc + curr.amount, 0);

  if (!displayClientAmountDueBox || !prices.length || !amountDue) {
    return null;
  }

  const isFinalPayment = isFinalPaymentStep(request);
  const label = t({ id: 'client-amount-due.label', message: 'Client amount due' });

  return (
    <Stack gap="0.5rem" ariaLabel={label}>
      <Box
        style={{
          backgroundColor: 'var(--color-warning-200)',
          borderColor: 'var(--color-warning-300)',
        }}
        padding="12px 16px"
        gap="0.25rem"
      >
        <Paragraph weight="medium">{label}</Paragraph>
        <ClientTransactionDetails prices={prices} />
      </Box>
      {!isFinalPayment && <ClientTransactionPartialPaymentWarning prices={prices} />}
    </Stack>
  );
};

const useDisplayClientAmountDueBox = () => {
  const { request, view: requestView } = useRequestContext();
  const { data: depositOptions } = useDepositOptions({
    requestId: request.id,
    enabled: isInDraftStep(request),
  });

  if (!requestView.organizationPrice.shown) {
    return false;
  }

  if (!request.articles.length) {
    return false;
  }

  if (isArchived(request)) {
    return false;
  }

  const hadOrWillHavePartialPayments = request.articles.some((article) =>
    article.steps?.some((step) => step.step === 'payment' && !!step.config.priceTypesToPay?.length)
  );

  if (!hadOrWillHavePartialPayments) {
    return false;
  }

  if (!isInDraftStep(request) && !isInPaymentStep(request) && !isInRefundStep(request)) {
    return false;
  }

  if (
    isInDraftStep(request) &&
    !!depositOptions &&
    !isRequiredPaidEstimateAdvanceDeposit(depositOptions) &&
    !request.deposits.length
  ) {
    return false;
  }

  return true;
};
